import {
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  FileDoneOutlined,
  SendOutlined,
  ShareAltOutlined,
} from "@ant-design/icons";
import { Button, Drawer, Skeleton, Space, Tag, Typography } from "antd";
import { hasRichText, richTextMarkup } from "../../pages/richTextUtils";
import "./JobDetailsDrawer.css";

const { Text, Title } = Typography;

const titleCase = (value) =>
  String(value || "")
    .replace(/[_-]/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const firstText = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number") return String(value);
  }
  return "";
};

const firstValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && String(value).trim() !== "") return value;
  }
  return null;
};

const namedRefLabel = (value) => {
  if (!value) return "";
  if (typeof value === "string") return titleCase(value);
  return firstText(value.name, value.label, value.title, value.code);
};

const toList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (value instanceof Set) return Array.from(value);
  return [value];
};

const normalizeStrings = (value) =>
  toList(value)
    .map((item) => (item && typeof item === "object" ? namedRefLabel(item) : titleCase(item)))
    .filter(Boolean);

const compactList = (...groups) => groups.flat().filter(Boolean);

const formatDate = (value) => {
  if (!value) return "Not set";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const formatMoney = (value, currency = "USD") => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value || "");
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: Number.isInteger(numeric) ? 0 : 2,
    }).format(numeric);
  } catch {
    return `$${numeric.toLocaleString()}`;
  }
};

const salaryLabel = (job) => {
  const direct = firstValue(
    job?.salaryExpectation,
    job?.salaryExpectations,
    job?.salaryRange,
    job?.compensation,
    job?.pay,
    job?.payRange,
  );
  if (direct) return String(direct);
  const currency = job?.currency || job?.salaryCurrency || job?.payCurrency || "USD";
  const min = firstValue(job?.salaryMin, job?.minSalary, job?.minimumSalary, job?.payMin, job?.minPay, job?.rateMin);
  const max = firstValue(job?.salaryMax, job?.maxSalary, job?.maximumSalary, job?.payMax, job?.maxPay, job?.rateMax);
  const single = firstValue(job?.salary, job?.payRate, job?.hourlyRate, job?.rate);
  const cadence = titleCase(firstValue(job?.salaryPeriod, job?.payPeriod, job?.rateUnit, job?.payFrequency));
  const suffix = cadence ? ` / ${cadence.toLowerCase()}` : "";
  if (min && max) return `${formatMoney(min, currency)} - ${formatMoney(max, currency)}${suffix}`;
  if (min) return `From ${formatMoney(min, currency)}${suffix}`;
  if (max) return `Up to ${formatMoney(max, currency)}${suffix}`;
  if (single) return `${formatMoney(single, currency)}${suffix}`;
  return "Not set";
};

const locationLabel = (location) => {
  if (!location) return "";
  if (typeof location === "string") return location;
  return firstText(
    location.label,
    location.name,
    location.address,
    location.formattedAddress,
    [location.city, location.state, location.country].filter(Boolean).join(", "),
  );
};

const statusColor = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "OPEN" || normalized === "ACTIVE") return "green";
  if (normalized === "DRAFT") return "gold";
  if (normalized === "CLOSED" || normalized === "ARCHIVED") return "red";
  return "blue";
};

const canApplyToJob = (job) =>
  !job?.applied && String(job?.status || "OPEN").toUpperCase() === "OPEN";

const JobDrawerActions = ({ job, onApply, onOpenDetails, onShare }) => (
  <div className="shared-job-details-drawer__actions">
    <Button
      type="primary"
      className="shared-job-details-drawer__apply-button"
      icon={<SendOutlined />}
      onClick={() => onApply?.(job)}
      disabled={!canApplyToJob(job)}
    >
      {job?.applied ? "Already Applied" : canApplyToJob(job) ? "Apply" : "Applications Closed"}
    </Button>
    {onOpenDetails ? (
      <Button className="shared-job-details-drawer__details-button" icon={<FileDoneOutlined />} onClick={() => onOpenDetails(job)}>
        Details
      </Button>
    ) : null}
    {onShare ? (
      <Button className="shared-job-details-drawer__share-button" icon={<ShareAltOutlined />} onClick={() => onShare(job)}>
        Share
      </Button>
    ) : null}
  </div>
);

const detailValue = (...values) => firstText(...values) || "Not set";

const JobDetailsDrawer = ({
  job,
  loading = false,
  open,
  onApply,
  onClose,
  onOpenDetails,
  onShare,
  showPosterDetails = true,
}) => {
  const skills = compactList(normalizeStrings(job?.skills || job?.requiredSkills), normalizeStrings(job?.otherRequiredSkills));
  const specializations = compactList(normalizeStrings(job?.specializations || job?.requiredSpecializations), normalizeStrings(job?.otherRequiredSpecializations));
  const workAreas = compactList(normalizeStrings(job?.requiredWorkAreas), normalizeStrings(job?.otherRequiredWorkAreas));
  const schedules = compactList(normalizeStrings(job?.workSchedules), job?.workSchedule ? [titleCase(job.workSchedule)] : []);
  const locations = toList(job?.requiredLocations).map(locationLabel).filter(Boolean);
  const descriptionSection = { label: "Description", value: job?.description };
  const detailItems = [
    { label: "Posted", value: formatDate(job?.createdAt), icon: <CalendarOutlined /> },
    { label: "Location type", value: titleCase(job?.locationType) || "Not set", icon: <EnvironmentOutlined /> },
    { label: "Schedule", value: schedules.join(", ") || "Not set", icon: <ClockCircleOutlined /> },
    { label: "Salary", value: salaryLabel(job), icon: <DollarOutlined /> },
  ];
  const sections = [
    { label: "Responsibilities", value: job?.responsibilities },
    { label: "Qualifications", value: job?.qualifications },
    { label: "Requirements", value: job?.requirements },
    { label: "Benefits", value: job?.benefits },
    { label: "How to apply", value: firstText(job?.applicationInstructions, job?.howToApply, job?.applyInstructions) },
  ].filter((item) => hasRichText(item.value) || firstText(item.value));
  const requirementGroups = [
    { label: "Skills", values: skills },
    { label: "Specializations", values: specializations },
    { label: "Work areas", values: workAreas },
  ].filter((group) => group.values.length);
  const posterDetails = [
    { label: "Company", value: detailValue(job?.workspace?.name, job?.workspace?.label, job?.workspaceName, job?.companyName, job?.organizationName) },
    { label: "Posted by", value: detailValue(job?.poster?.name, job?.poster?.displayName, job?.poster?.fullName, job?.posterName, titleCase(job?.posterType)) },
  ].filter((item) => item.value && item.value !== "Not set");

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      width="min(620px, 100vw)"
      title={job ? <Tag color={statusColor(job?.status)}>{titleCase(job?.status || "Open")}</Tag> : null}
      className="shared-job-details-drawer"
      extra={job ? <JobDrawerActions job={job} onApply={onApply} onOpenDetails={onOpenDetails} onShare={onShare} /> : null}
    >
      {loading ? (
        <Skeleton active paragraph={{ rows: 5 }} />
      ) : !job ? (
        <div className="shared-job-details-drawer__empty">
          <Text>Job details are not available.</Text>
        </div>
      ) : (
        <Space direction="vertical" size={9} className="shared-job-details-drawer__content">
          <div className="shared-job-details-drawer__hero">
            <Title level={4}>{job?.title || "Untitled job post"}</Title>
            {job?.applied ? <Tag color="green" icon={<CheckCircleOutlined />}>Applied</Tag> : null}
          </div>

          <div className="shared-job-details-drawer__grid">
            {detailItems.map((item) => (
              <div key={item.label} className="shared-job-details-drawer__fact">
                <span>{item.icon}</span>
                <div>
                  <Text type="secondary">{item.label}</Text>
                  <strong>{item.value}</strong>
                </div>
              </div>
            ))}
          </div>

          {requirementGroups.map((group) => (
            <div key={group.label} className="shared-job-details-drawer__block">
              <Text type="secondary">{group.label}</Text>
              <Space wrap size={[4, 4]}>
                {group.values.map((item) => <Tag key={item}>{item}</Tag>)}
              </Space>
            </div>
          ))}

          {locations.length ? (
            <div className="shared-job-details-drawer__block">
              <Text type="secondary">Locations</Text>
              <Space wrap size={[4, 4]}>
                {locations.map((item) => <Tag key={item}>{item}</Tag>)}
              </Space>
            </div>
          ) : null}

          {hasRichText(descriptionSection.value) || firstText(descriptionSection.value) ? (
            <div className="shared-job-details-drawer__section">
              <Text type="secondary">{descriptionSection.label}</Text>
              {hasRichText(descriptionSection.value) ? (
                <div className="olreadia-rich-text" dangerouslySetInnerHTML={richTextMarkup(descriptionSection.value)} />
              ) : (
                <Text>{descriptionSection.value}</Text>
              )}
            </div>
          ) : null}

          {showPosterDetails && posterDetails.length ? (
            <div className="shared-job-details-drawer__section">
              {/* <Text type="secondary">Poster and company</Text> */}
              <div className="shared-job-details-drawer__poster-grid">
                {posterDetails.map((item) => (
                  <div key={item.label}>
                    <Text type="secondary">{item.label}</Text>
                    <strong>{item.value}</strong>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {sections.map((section) => (
            <div key={section.label} className="shared-job-details-drawer__section">
              <Text type="secondary">{section.label}</Text>
              {hasRichText(section.value) ? (
                <div className="olreadia-rich-text" dangerouslySetInnerHTML={richTextMarkup(section.value)} />
              ) : (
                <Text>{section.value}</Text>
              )}
            </div>
          ))}

          <JobDrawerActions job={job} onApply={onApply} onOpenDetails={onOpenDetails} onShare={onShare} />
        </Space>
      )}
    </Drawer>
  );
};

export default JobDetailsDrawer;
